/* eslint-disable @next/next/no-img-element */
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, Settings } from '@styled-icons/material';
import { ArrowLeft } from '@styled-icons/feather';
import { useRouter } from 'next/router';
import { errToString, ISpace, ISpaceRoute, validUUID } from '@typedream/data';
import { Check, Search } from '@styled-icons/material-outlined';
import { useSpaceIDFromPath } from '@/data/space/hooks';
import { useCreatePageMutation, useGetSpaceQuery, useListSpacesQuery } from '@/store';
import getAvatar from '@/utils/getAvatar';
import SidebarItem from '@/components/SidebarItem';
import { HoverCard } from '@/sharedComponents/v1/Components/Dropdown';
import Avatar from '@/sharedComponents/v1/Components/Avatar';
import { LOGO_DOMAIN } from '@/constants';
import { toast } from 'react-toastify';
import { Text } from '@typedream/ui';
import Link from 'next/link';
import classNames from 'classnames';
import styles from '../../PageSidebar/PageSidebar.module.css';

function SpaceTitle() {
  const spaceID = useSpaceIDFromPath();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { mutate: createPage } = useCreatePageMutation({ fixedCacheKey: `createPage-${spaceID}` });

  const [avatar, setavatar] = useState('');
  const router = useRouter();
  const [openOptions, setopenOptions] = useState(false);

  const [open, setopen] = useState(false);
  const [search, setsearch] = useState('');
  const [currSpace, setCurrSpace] = useState<ISpace>();
  const [mutatedSpaceList, setMutatedSpaceList] = useState<ISpace[]>();

  const { data: spaceList, isLoading: isLoadingSpaceList } = useListSpacesQuery([], {
    skip: !spaceID,
  });
  const ref = useRef(null);

  const handleOnClickSpace = (space: ISpace) => {
    router.push(`/s/${space?.slug}/${space?.id}/${space?.routes?.page_id}`);
    setopen(false);
  };

  const route = space?.routes;

  const onCreate = async (
    spaceRoute: ISpaceRoute,
    path: string[],
    setopen: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      const { page: newPage } = await createPage({
        spaceID,
        pagePath: path,
        routeAtPath: spaceRoute,
      }).unwrap();

      if (validUUID(newPage?.id)) {
        setopen(true);
        setopenOptions(false);
        const spaceSlug = space?.slug;
        router.push(`/s/${spaceSlug}/${spaceID}/${newPage?.id}`);
      }
    } catch (error) {
      toast.error(errToString(error));
    }
  };

  const handleOnClickAddNew = () => {
    router.push('/onboarding?createNewWorkspace=true');
  };

  useEffect(() => {
    if (spaceList) {
      const activeSpace = spaceList?.[String(spaceID)];
      setCurrSpace(activeSpace);

      const arrOfSpace = Object.keys(spaceList)
        .filter((key) => spaceList?.[key]?.id !== String(spaceID))
        .map((filteredKey) => spaceList?.[filteredKey]);
      setMutatedSpaceList([activeSpace, ...arrOfSpace]);
    }
  }, [spaceList, spaceID]);

  const searchedSpaceList = useMemo(
    () =>
      (mutatedSpaceList || [])
        .filter((v) => !!v)
        .filter(
          ({ name, slug, metadata }) =>
            (slug.toLowerCase().includes(search.trim().toLowerCase()) ||
              name.toLowerCase().includes(search.trim().toLowerCase())) &&
            metadata?.site_type !== 'team'
        ),
    [mutatedSpaceList, search]
  );

  useEffect(() => {
    setavatar(getAvatar(spaceID));
  }, [spaceID]);

  if (isLoadingSpaceList) return null;

  return (
    <HoverCard
      trigger={
        <div className={styles.spaceTitleWrapper}>
          <div className={styles.spaceName}>
            <img
              alt="icon"
              src={space?.icon || avatar}
              width={20}
              height={20}
              className={styles.icon}
              style={{
                borderRadius: space?.icon ? 0 : 999,
              }}
            />
            <span>{space?.name}</span>
          </div>
        </div>
      }
      onOpenChange={(o: boolean) => {
        if (o) {
          setTimeout(() => {
            ref.current?.focus();
          }, 100);
        }
      }}
    >
      <div className={styles.settingsContainer}>
        <div className="flex justify-between w-full">
          <Text variant="secondary" size="2xs" weight="medium">
            Switch Workspace
          </Text>
          <Text variant="secondary" size="2xs" weight="medium">
            <Link href="/dashboard/sites" target="_blank">
              See All
            </Link>
          </Text>
        </div>
        <div className="py-3 px-[18px] w-full">
          <div className="flex relative">
            <Search
              size={20}
              color="var(--deprecated-neutral-600)"
              className="absolute left-[18px] translate-y-1/2"
            />
            <input
              placeholder="Search workspace"
              ref={ref}
              className={[
                'w-full py-2.5 pr-[18px] pl-11 shadow-[var(--shadow-xs)] bg-[var(--deprecated-neutral-100)] border-0 rounded-lg',
                'font-medium text-sm leading-normal',
                'placeholder-[var(--deprecated-neutral-600)]',
              ].join(' ')}
              value={search}
              onChange={(e) => setsearch(e.target.value)}
            />
          </div>
        </div>
        <div className="h-[300px] overflow-y-auto overflow-x-hidden py-1 w-full">
          {!searchedSpaceList.length && (
            <div className="w-full h-full flex justify-center items-center px-5 flex-col">
              <Text as="p" variant="main" size="sm" weight="semibold">
                No results 😔
              </Text>
              <Text as="p" variant="mute" className="text-center" size="xs" weight="medium">
                We couldn’t find anything for "{search}" in Your workspace list. Try refine your
                search term!
              </Text>
            </div>
          )}
          {searchedSpaceList.map((space) => (
            <div
              key={space?.id}
              className="py-2.5 flex items-center gap-2.5 cursor-pointer hover:opacity-50 transition"
              onClick={() => handleOnClickSpace(space)}
              aria-hidden
            >
              <div>
                <Avatar size={36} src={space?.icon || getAvatar(String(space?.id))} />
              </div>
              <div className="w-[192px]">
                <Text
                  variant="main"
                  size="sm"
                  weight="semibold"
                  className={classNames('text-ellipsis overflow-hidden whitespace-nowrap', {
                    'text-[var(--accent)]': currSpace?.id === space.id,
                  })}
                  as="p"
                >
                  {space?.name}
                </Text>
                <Text
                  size="xs"
                  as="p"
                  weight="medium"
                  variant="mute"
                  className="text-ellipsis overflow-hidden whitespace-nowrap"
                >
                  {space?.custom_domain || `${space?.slug}.${LOGO_DOMAIN}`}
                </Text>
              </div>
              {currSpace?.id === space.id && <Check size={20} color="var(--accent)" />}
            </div>
          ))}
        </div>

        <hr className={styles.hr} />
        <div className="pt-0">
          <Text variant="secondary" size="2xs" weight="medium">
            Current Workspace
          </Text>
        </div>
        <SidebarItem
          link={`/dashboard/${spaceID}`}
          left={<ArrowLeft size={20} />}
          center="Go to Dashboard"
          className={styles.sidebarSpaceOption}
        />
        <SidebarItem
          link={`/settings/${space?.slug}/${spaceID}/site`}
          className={styles.sidebarSpaceOption}
          left={<Settings size={20} />}
          center="Settings"
        />
        <SidebarItem
          link={`/settings/${space?.slug}/${spaceID}/billing`}
          left={<CreditCard size={20} />}
          center="Billing"
          className={styles.sidebarSpaceOption}
        />
      </div>
    </HoverCard>
  );
}

export default SpaceTitle;
